import { ReactNode, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { DPCalendar } from '@Components/DatePicker/DPCalendar';
import { FOCUS_OPTIONS } from '@Components/DatePicker/DatePickerButton/DatePickerConstants';
import { Dropdown } from '@Components/Dropdown';
import { Placement, Size } from '@Components/designConstants';
import {
  CALENDAR_YEARS,
  DATE_OPTIONS,
  DatePreset,
  isDatePreset
} from './activity-feed-filters-constants';
import { FilterSection } from './FilterSection';

// Only include the keys you're changing — the parent's spread merge keeps prev values for any
// keys you omit. `null` is still the explicit "clear this field" signal.
type DatePatch = Partial<{
  date: DatePreset | null;
  customStartDate: string | null;
  customEndDate: string | null;
}>;

interface DateFilterSectionProps {
  date: DatePreset | null;
  customStartDate: string | null;
  customEndDate: string | null;
  onChange: (input: DatePatch) => void;
}

// Dropdown only supports a leadingIcon today, so the toggle uses its default chevron.
export const DateFilterSection = ({
  date,
  customStartDate,
  customEndDate,
  onChange
}: DateFilterSectionProps): ReactNode => {
  const [calendarFocus, setCalendarFocus] = useState<(typeof FOCUS_OPTIONS)[number]>(
    FOCUS_OPTIONS[0]
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Ref-through-state so <Overlay target> can read the DOM node.
  const [dropdownEl, setDropdownEl] = useState<HTMLDivElement | null>(null);

  const handleSelect = (next: string) => {
    if (next === DatePreset.CUSTOM) {
      // Just flip the preset — customStartDate/End stay whatever they were in state.
      onChange({ date: DatePreset.CUSTOM });
      setCalendarFocus(FOCUS_OPTIONS[0]);
      setCalendarOpen(true);
      return;
    }
    // Leave stale custom range in state — applyFilters ignores it while date !== CUSTOM, and
    // preserving it lets a user preview another preset and jump back to Custom without redrawing.
    if (!isDatePreset(next)) return;
    onChange({ date: next });
    setCalendarOpen(false);
  };

  const handleCalendarChange = (
    dateRange: { start?: string; end?: string },
    focus: (typeof FOCUS_OPTIONS)[number]
  ) => {
    setCalendarFocus(focus);
    onChange({
      date: DatePreset.CUSTOM,
      customStartDate: dateRange.start ?? null,
      customEndDate: dateRange.end ?? null
    });
    if (dateRange.start && dateRange.end) {
      setCalendarOpen(false);
    }
  };

  return (
    <FilterSection label='Date'>
      <div ref={setDropdownEl}>
        <Dropdown<string>
          size={Size.LG}
          overlayPlacement={Placement.RIGHT}
          options={DATE_OPTIONS}
          value={date ?? undefined}
          placeholder='All dates'
          clearable
          onClear={() => {
            onChange({ date: null, customStartDate: null, customEndDate: null });
            setCalendarOpen(false);
          }}
          onChange={handleSelect}
        />
      </div>
      <Overlay target={dropdownEl} show={calendarOpen} placement={Placement.RIGHT_START}>
        <Popover id='activity-feed-date-popover'>
          <Popover.Body className='activity-feed-date-popover-body'>
            <DPCalendar
              start={customStartDate ?? undefined}
              end={customEndDate ?? undefined}
              focus={calendarFocus}
              onChange={handleCalendarChange}
              years={CALENDAR_YEARS}
              numVisibleMonths={1}
            />
          </Popover.Body>
        </Popover>
      </Overlay>
    </FilterSection>
  );
};
